import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Settings } from "lucide-react";
import { useUser } from "@/hooks/useUser";
import { getCurrentUserOrganisation } from "@/utils/roleUtils";

export interface SpiTooltipContentProps {
  onTimeDelayMax?: number;
  moderateDelayMax?: number;
  organisationId?: string;
  calculationBasis?: "saved_task_baseline" | "current_task_schedule";
}

export const SpiTooltipContent: React.FC<SpiTooltipContentProps> = ({
  onTimeDelayMax: propOnTimeDelayMax,
  moderateDelayMax: propModerateDelayMax,
  organisationId: propOrgId,
  calculationBasis = "current_task_schedule",
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const userOrg = getCurrentUserOrganisation(user);

  const onTimeDelayMax = propOnTimeDelayMax ?? userOrg?.organisation?.onTimeDelayMax ?? 5;
  const moderateDelayMax = propModerateDelayMax ?? userOrg?.organisation?.moderateDelayMax ?? 20;
  const orgId = propOrgId ?? userOrg?.organisationId ?? "";

  const lowThreshold = 100 - moderateDelayMax;
  const midThreshold = 100 - onTimeDelayMax;

  // Decimal thresholds (e.g. 0.90, 0.95, 1.00)
  const lowThresholdDec = (lowThreshold / 100).toFixed(2);
  const midThresholdDec = (midThreshold / 100).toFixed(2);

  const basisText =
    calculationBasis === "saved_task_baseline"
      ? t("calculation_basis_saved_task_baseline")
      : t("calculation_basis_current_task_schedule");

  return (
    <div className="p-3 text-slate-800 max-w-sm text-left normal-case space-y-3 font-normal">
      <div>
        <div className="font-bold text-sm text-slate-900">
          {t("schedule_performance_index_spi")}
        </div>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          {t("spi_desc")}
        </p>
        <p className="text-[11px] text-slate-400 mt-1 italic font-medium">
          {basisText}
        </p>
      </div>

      <div className="space-y-2 text-xs">
        {/* Row 1: Significantly behind schedule */}
        <div className="flex items-center justify-between gap-4">
          <span className="font-bold text-slate-700">
            SPI &lt; {lowThresholdDec}
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FEE2E2]/70 text-[#DC2626] border border-[#FECACA]/80 whitespace-nowrap">
            {t("spi_significantly_behind")}
          </span>
        </div>

        {/* Row 2: Moderately behind schedule */}
        <div className="flex items-center justify-between gap-4">
          <span className="font-bold text-slate-700">
            {lowThresholdDec} &le; SPI &lt; {midThresholdDec}
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FEF3C7]/60 text-[#D97706] border border-[#FDE68A]/80 whitespace-nowrap">
            {t("spi_moderately_behind")}
          </span>
        </div>

        {/* Row 3: On schedule */}
        <div className="flex items-center justify-between gap-4">
          <span className="font-bold text-slate-700">
            {midThresholdDec} &le; SPI &le; 1.00
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0]/60 whitespace-nowrap">
            {t("spi_on_schedule")}
          </span>
        </div>

        {/* Row 4: Ahead of schedule */}
        <div className="flex items-center justify-between gap-4">
          <span className="font-bold text-slate-700">
            SPI &gt; 1.00
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#EBF3FF] text-[#2563EB] border border-[#BFDBFE]/60 whitespace-nowrap">
            {t("spi_ahead_of_schedule")}
          </span>
        </div>
      </div>

      <div className="text-[11px] text-slate-400 font-medium">
        {t("spi_ratio_scale_note")}
      </div>

      {/* Footer link to KPI Settings */}
      <div className="pt-2 border-t border-slate-100 flex items-center gap-1.5 text-[11px] text-slate-500">
        <Settings className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span>
          {t("thresholds_configured_in")}{" "}
          {orgId ? (
            <Link
              to={`/organisation/${orgId}/kpi-settings`}
              className="text-[#535EF9] hover:underline font-medium"
            >
              {t("kpi_settings")}
            </Link>
          ) : (
            <span className="text-[#535EF9] font-medium">{t("kpi_settings")}</span>
          )}
          .
        </span>
      </div>
    </div>
  );
};
